// Página de listagem de clientes
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Trash2, Eye, AlertCircle } from 'lucide-react';
import { Select } from '../../../components/common/Select';
import { getClientes, createCliente, updateCliente, deleteCliente } from '../../../services/clientesService';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCPF, formatCNPJ, formatDate } from '../../../utils/formatters';
import { supabase } from '../../../lib/supabase';
import type { Cliente } from '../../../types';

export function Clientes() {
  const { empresaSelecionada } = useEmpresa();
  const { canUpdate, canDelete } = usePermissions();
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clienteSelecionado, setClienteSelecionado] = useState<Cliente | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Carregar clientes
  useEffect(() => {
    if (empresaSelecionada) {
      carregarClientes();
    }
  }, [empresaSelecionada]);

  const carregarClientes = async () => {
    if (!empresaSelecionada) return;
    
    try {
      setLoading(true);
      setError(null);
      const dados = await getClientes(empresaSelecionada.id);
      setClientes(dados);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar clientes');
      console.error('Erro ao carregar clientes:', err);
    } finally {
      setLoading(false);
    }
  };

  const clientesFiltrados = clientes.filter(c =>
    c.nome.toLowerCase().includes(busca.toLowerCase()) ||
    c.cpf?.includes(busca) ||
    c.cnpj?.includes(busca) ||
    c.email.toLowerCase().includes(busca.toLowerCase())
  );

  const abrirModal = (cliente?: Cliente) => {
    setClienteSelecionado(cliente || null);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setClienteSelecionado(null);
  };

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir clientes');
      return;
    }

    if (!confirm('Tem certeza que deseja excluir este cliente?')) {
      return;
    }

    try {
      await deleteCliente(id);
      await carregarClientes();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir cliente');
      console.error('Erro ao excluir cliente:', err);
    }
  };

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Clientes' }]} />
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Selecione uma empresa para visualizar os clientes</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Clientes' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Clientes' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Clientes</h1>
        <Button onClick={() => abrirModal()} variant="primary">
          <Plus className="w-4 h-4 mr-2" />
          Novo Cliente
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}
      
      {/* Busca */}
      <Card>
        <Input
          label="Buscar Cliente"
          placeholder="Nome, CPF, CNPJ ou email"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Card>
      
      {/* Tabela */}
      <Card>
        <Table
          data={clientesFiltrados}
          columns={[
            { key: 'nome', label: 'Nome' },
            { 
              key: 'tipo', 
              label: 'Tipo', 
              render: (item) => item.tipo === 'F' ? 'Pessoa Física' : 'Pessoa Jurídica' 
            },
            { 
              key: 'cpf', 
              label: 'CPF/CNPJ', 
              render: (item) => item.tipo === 'F' ? formatCPF(item.cpf || '') : formatCNPJ(item.cnpj || '') 
            },
            { key: 'email', label: 'Email' },
            { key: 'telefone', label: 'Telefone' },
            { 
              key: 'createdAt', 
              label: 'Cadastrado em', 
              render: (item) => formatDate(item.createdAt) 
            },
            {
              key: 'actions',
              label: 'Ações',
              render: (item) => (
                <div className="flex items-center gap-2">
                  {canUpdate && (
                    <button
                      onClick={() => abrirModal(item)}
                      className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => console.log('Visualizar', item)}
                    className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
      
      {/* Modal de Cadastro/Edição */}
      <Modal
        isOpen={isModalOpen}
        onClose={fecharModal}
        title={clienteSelecionado ? 'Editar Cliente' : 'Novo Cliente'}
        size="lg"
      >
        <ClienteForm 
          cliente={clienteSelecionado} 
          onClose={fecharModal}
          onSuccess={carregarClientes}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      </Modal>
    </div>
  );
}

// Componente de formulário de cliente
function ClienteForm({ 
  cliente, 
  onClose, 
  onSuccess,
  isSaving,
  setIsSaving
}: { 
  cliente: Cliente | null; 
  onClose: () => void;
  onSuccess: () => void;
  isSaving: boolean;
  setIsSaving: (value: boolean) => void;
}) {
  const { empresaSelecionada, contaId } = useEmpresa();
  const [formData, setFormData] = useState({
    nome: cliente?.nome || '',
    tipo: cliente?.tipo || 'F',
    cpf: cliente?.cpf || '',
    cnpj: cliente?.cnpj || '',
    email: cliente?.email || '',
    telefone: cliente?.telefone || '',
    cep: cliente?.endereco.cep || '',
    logradouro: cliente?.endereco.logradouro || '',
    numero: cliente?.endereco.numero || '',
    complemento: cliente?.endereco.complemento || '',
    bairro: cliente?.endereco.bairro || '',
    cidade: cliente?.endereco.cidade || '',
    estado: cliente?.endereco.estado || '',
  });
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!empresaSelecionada) {
      setError('Empresa não selecionada');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const clienteData = {
        nome: formData.nome,
        tipo: formData.tipo as 'F' | 'J',
        cpf: formData.tipo === 'F' ? formData.cpf : undefined,
        cnpj: formData.tipo === 'J' ? formData.cnpj : undefined,
        email: formData.email,
        telefone: formData.telefone,
        endereco: {
          cep: formData.cep,
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
        },
      };

      // Buscar conta_id se não estiver no contexto
      let contaIdParaUsar = contaId;
      if (!contaIdParaUsar && empresaSelecionada) {
        try {
          const { data: empresaData, error } = await (supabase as any)
            .from('empresas')
            .select('conta_id')
            .eq('id', empresaSelecionada.id)
            .single();
          
          if (!error && empresaData?.conta_id) {
            contaIdParaUsar = empresaData.conta_id;
          } else {
            throw new Error('Empresa não possui conta vinculada. Por favor, vincule uma conta à empresa no módulo Admin.');
          }
        } catch (err: any) {
          throw new Error('Erro ao buscar conta da empresa. Por favor, vincule uma conta à empresa no módulo Admin.');
        }
      }

      if (!contaIdParaUsar) {
        throw new Error('Empresa não possui conta vinculada. Por favor, vincule uma conta à empresa no módulo Admin.');
      }

      if (cliente) {
        await updateCliente(cliente.id, clienteData);
      } else {
        await createCliente(clienteData, contaIdParaUsar, empresaSelecionada.id);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar cliente');
      console.error('Erro ao salvar cliente:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome"
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          required
          disabled={isSaving}
        />
        <Select
          label="Tipo"
          value={formData.tipo}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFormData({ ...formData, tipo: e.target.value as 'F' | 'J' })}
          options={[
            { value: 'F', label: 'Pessoa Física' },
            { value: 'J', label: 'Pessoa Jurídica' },
          ]}
          required
          disabled={isSaving}
        />
        {formData.tipo === 'F' ? (
          <Input
            label="CPF"
            value={formData.cpf}
            onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
            required
            disabled={isSaving}
          />
        ) : (
          <Input
            label="CNPJ"
            value={formData.cnpj}
            onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
            required
            disabled={isSaving}
          />
        )}
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Telefone"
          value={formData.telefone}
          onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="CEP"
          value={formData.cep}
          onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
          required
          disabled={isSaving}
        />
        <div className="md:col-span-2">
          <Input
            label="Logradouro"
            value={formData.logradouro}
            onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
            required
            disabled={isSaving}
          />
        </div>
        <Input
          label="Número"
          value={formData.numero}
          onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Complemento"
          value={formData.complemento}
          onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
          disabled={isSaving}
        />
        <Input
          label="Bairro"
          value={formData.bairro}
          onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Cidade"
          value={formData.cidade}
          onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Estado"
          value={formData.estado}
          onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
          required
          disabled={isSaving}
        />
      </div>
      
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
